import { useQuery } from '@tanstack/react-query';
import { chain } from 'lodash';
import {
  getIsoCountryCodesOptions,
  getProductTypeByCountryOptions,
} from 'src/clients/locations/Device Locations API/@tanstack/react-query.gen';
import {
  CslCmpDeviceLocationsApiFeaturesGetProductTypeByCountryGetProductTypeByCountryRecordV1,
  CslCmpDeviceLocationsApiFeaturesMetaDataGetIsoCountryCodesGetIsoCountryCodeItemV1,
  CslCmpDeviceLocationsApiFeaturesMetaDataGetIsoCountryCodesGetIsoCountryCodesResponseV1,
} from 'src/clients/locations/Device Locations API/types.gen';

type GetCountryListOptions = {
  accountReferenceId: string;
};

type CountryListItem =
  CslCmpDeviceLocationsApiFeaturesMetaDataGetIsoCountryCodesGetIsoCountryCodeItemV1;

type UseGetCountryListResult =
  | { status: 'success'; data: CountryListItem[] }
  | { status: 'pending'; data: undefined }
  | { status: 'error'; error: unknown; data: undefined };

function uniqueCountryList(
  data: CslCmpDeviceLocationsApiFeaturesGetProductTypeByCountryGetProductTypeByCountryRecordV1[],
  isoCountries: CslCmpDeviceLocationsApiFeaturesMetaDataGetIsoCountryCodesGetIsoCountryCodesResponseV1,
): CountryListItem[] {
  const uniqueCountries = chain(data)
    .map((i) => i.country)
    .compact()
    .uniq()
    .value();

  const countryList = uniqueCountries
    .map((countryCode) => {
      const countryName = isoCountries.items?.find(
        (i) => i.countryCode === countryCode,
      )?.countryName;

      if (countryName) {
        return {
          countryName,
          countryCode,
        };
      }

      return null;
    })
    .filter((i) => i !== null);

  return countryList;
}

export const useGetCountryList = (options: GetCountryListOptions): UseGetCountryListResult => {
  const { accountReferenceId } = options;
  const productTypeByCountryQueryOptions = getProductTypeByCountryOptions({
    query: {
      accountReferenceId,
    },
  });

  const isoCountryOptions = getIsoCountryCodesOptions();

  const productTypeByCountryQuery = useQuery({
    ...productTypeByCountryQueryOptions,
    throwOnError: true,
    enabled: !!accountReferenceId,
    meta: { isLocations: true },
  });

  const isoCountryQuery = useQuery({
    ...isoCountryOptions,
    throwOnError: true,
  });

  if (productTypeByCountryQuery.status === 'success' && isoCountryQuery.status === 'success') {
    const data = uniqueCountryList(productTypeByCountryQuery.data, isoCountryQuery.data);

    return {
      status: 'success',
      data,
    };
  }

  if (productTypeByCountryQuery.status === 'pending' || isoCountryQuery.status === 'pending') {
    return { status: 'pending', data: undefined };
  }

  return {
    status: 'error',
    error: productTypeByCountryQuery.error ?? isoCountryQuery.error ?? new Error('Unknown error'),
    data: undefined,
  };
};
